import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  updateSupabaseInvoice,
  fetchOneSupabaseInvoice,
} from "store/InvoiceSlice";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";

export default function ChangeStatusModal({ open, onClose, invoice }) {
  const dispatch = useDispatch();
  const { userId } = useSupabaseAuth();
  const [sentToClient, setSentToClient] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open && invoice) {
      setSentToClient(invoice.sent || false);
      setPaymentStatus(invoice.paid || "");
      setSelectedStatus(invoice.status || "");
      setError(null);
    } else if (!open) {
      setSentToClient(false);
      setPaymentStatus("");
      setSelectedStatus("");
      setError(null);
      setProcessing(false);
    }
  }, [open, invoice]);

  const handleSentToClientChange = (event) => {
    setSentToClient(event.target.value);
  };

  const handlePaymentStatusChange = (event) => {
    setPaymentStatus(event.target.value);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleConfirm = async () => {
    if (!invoice?.id) {
      setError("Invoice ID is required for status update.");
      return;
    }

    if (!selectedStatus) {
      setError("Please select a status.");
      return;
    }

    if (paymentStatus === "") {
      setError("Please select a payment status.");
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const result = await dispatch(
        updateSupabaseInvoice({
          id: invoice.id,
          data: {
            sent: sentToClient,
            paid: paymentStatus,
            status: selectedStatus,
          },
        })
      );

      if (updateSupabaseInvoice.fulfilled.match(result)) {
        // Refresh the invoice data after update
        if (userId) {
          await dispatch(fetchOneSupabaseInvoice({ id: invoice.id, userId }));
        }
        setProcessing(false);
        onClose();
      } else {
        const errorMessage =
          result.payload ||
          "Failed to update invoice status. Please try again.";
        setError(errorMessage);
        setProcessing(false);
      }
    } catch (err) {
      setError(
        err.message || "Failed to update invoice status. Please try again."
      );
      setProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="change-status-dialog-title"
      aria-describedby="change-status-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="change-status-dialog-title">
        <Typography variant="h3">Change InvoiceStatus</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {processing && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                backgroundColor: "action.hover",
                borderRadius: 1,
              }}
            >
              <CircularProgress size={24} />
              <Typography>Updating status...</Typography>
            </Box>
          )}

          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                onChange={handleStatusChange}
                label="Status"
                disabled={processing}
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Sent to Client</InputLabel>
              <Select
                value={sentToClient}
                onChange={handleSentToClientChange}
                label="Sent to Client"
                disabled={processing}
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={paymentStatus}
                onChange={handlePaymentStatusChange}
                label="Payment Status"
                disabled={processing}
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="no">No (not yet)</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="na">NA</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={
            !selectedStatus ||
            paymentStatus === "" ||
            processing ||
            !invoice?.id
          }
        >
          {processing ? "Updating..." : "Update Status"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
